import {useContext} from 'react';
import {Stack} from '../../shared/layouts';
import Header from '../../shared/Header';
import Card from '../../shared/Card';
import {useTheme} from '@emotion/react';
import {useMediaQuery} from '@dnhb/node_package';
import DisplayLine from '../../shared/DisplayLine';
import {useQuery} from 'react-query';
import {isEmpty} from 'lodash';
import axios from 'axios';
import Loader from '../../shared/Loader';
import Placeholder from '../../shared/Placeholder';
import MessagebarContext from '../../../context/MessagebarContext';
import moment from "moment";
import {useTranslation} from "react-i18next";
import useIconLibrary from "../../../hooks/useIconLibrary";

const formatTime = (time) => {
  const momentTime = moment(time, 'HH:mm');
  return momentTime.isValid() ? momentTime.format('HH:mm') : time;
};

const ContactPage = () => {
  const { breakpoints } = useTheme();
  const { t } = useTranslation();
  const isMinDesktop = useMediaQuery(
    `(min-width: ${breakpoints.ml ?? '56.25rem'})`
  );
  const { setAlertMessage } = useContext(MessagebarContext);
  const { isLoading, data: { location = {}, contact_persons = [] } = {} } =
    useQuery(
      'contactInformation',
      async () => {
        try {
          const {
            data: { contactInformation },
          } = await axios('/contact-information');

          if (!contactInformation) {
            return {};
          }

          contactInformation.location = contactInformation?.locations?.[0] || {};

          return contactInformation;
        } catch (error) {
          setAlertMessage();
        }
      },
      { refetchOnWindowFocus: false, retry: false }
    );

  const {
    name,
    street,
    number,
    suffix,
    postalcode,
    city,
    email,
    phone,
    openingshours,
  } = location;
  const hasLocation = !isEmpty(location);
  const hasPersons = contact_persons.length > 0;
  const AddressLines = [
    ...(street && number ? [`${street} ${number}${suffix ?? ''},`] : []),
    ...(postalcode && city ? [`${postalcode} ${city}`] : []),
  ];
  const { ContactIcon, ProfileIcon } = useIconLibrary();

  return (
    <>
      {isLoading && <Loader />}
      <Stack space="xl" width={{ sm: 656, ml: 672, lg: 800, xl: 1024 }}>
        <Header
          title={t('Contact')}
          icon={<ContactIcon height="2rem" width="2rem" />}
          variant="page"
        />
        {!hasLocation && !hasPersons && (
          <Placeholder>{t("Er zijn geen contactgegevens beschikbaar")}</Placeholder>
        )}
        {hasLocation && (
          <Card>
            <Header
              title={name}
              icon={<ContactIcon height="2rem" width="2rem" />}
              variant="section"
            />
            <Stack
              space={isMinDesktop ? 'm' : 's'}
              mt={{ mobile: 'm', desktop: 'xl' }}
              mb={{ mobile: 'm', desktop: 'l' }}
            >
              <DisplayLine label={t("Adres")} value={AddressLines} />
              <DisplayLine label={t("E-mail")} value={email} linkify />
              <DisplayLine label={t("Telefoon")} value={phone} />
            </Stack>
            {openingshours?.length > 0 && (
              <Stack
                space={isMinDesktop ? 'm' : 's'}
                pt={{ mobile: 'm', desktop: 'l' }}
                borderTop="default"
              >
                <strong>Openingstijden:</strong>
                {openingshours.map(({ day, from, to }) => {
                  const fromTime = formatTime(from);
                  const toTime = formatTime(to);

                  return (
                    <DisplayLine
                      key={day}
                      label={day}
                      value={`${fromTime} - ${toTime}`}
                    />
                  );
                })}
              </Stack>
            )}
          </Card>
        )}
        {hasPersons && (
          <Card>
            <Header
              title={t("Team")}
              icon={<ProfileIcon height="2rem" width="2rem" />}
              variant="section"
            />
            <Stack space={isMinDesktop ? 'l' : 'm'}>
              {contact_persons.map(({ name, role, email, phone, photo }, index) => {
                const firstMember = index === 0;
                return (
                  <Stack
                    key={name}
                    space={isMinDesktop ? 'm' : 's'}
                    pt={{ mobile: 'm', desktop: firstMember ? 'xl' : 'l' }}
                    borderTop={!firstMember && 'default'}
                  >
                    <div style={{display: "flex", flexDirection: isMinDesktop ? "row" : "column"}}>
                      <div style={ isMinDesktop ? {marginRight: 20, width: "100%"} : {width: "100%"}}>
                        <DisplayLine label={name} value={role ?? ''} />
                        <DisplayLine label={t("E-mail")} value={email} linkify />
                        <DisplayLine label={t("Telefoon")} value={phone} />
                      </div>
                      {photo && (
                        <img
                          style={ isMinDesktop ? {marginLeft: "auto", marginRight: 20} : {marginLeft: "auto", marginRight: "auto", marginTop: 10}}
                          src={'data:image/png;base64,' + photo}
                          alt={t('{{name}} profiel foto', {
                            name: name
                          })}
                          width={'116'}
                        />
                      )}
                    </div>
                  </Stack>
                );
              })}
            </Stack>
          </Card>
        )}
      </Stack>
    </>
  );
};

export default ContactPage;
